import type { CategoryDto, CategoryRefSource, ParentCategory } from '@/types';

/**
 * The category taxonomy as a real tree. The server DTO flattens each root's
 * whole subtree into `ParentCategory.children` (every entry carrying its
 * IMMEDIATE `parentId`), which is enough for 2 levels but drops the middle
 * level of Pluggy's 3-deep branches everywhere it's consumed. This module
 * reconstructs the hierarchy once so consumers can ask for depth, ancestry and
 * breadcrumb paths instead of re-deriving them ad hoc.
 */
export interface CategoryNode {
  id: string;
  label: string;
  iconName: string | null;
  /** Immediate parent; null for roots. */
  parentId: string | null;
  rootId: string;
  /** 1 = root, 2 = child, 3 = grandchild (Pluggy's maximum today). */
  depth: number;
  children: CategoryNode[];
}

export interface CategoryTree {
  roots: readonly CategoryNode[];
  byId: ReadonlyMap<string, CategoryNode>;
}

export function buildCategoryTree(parents: readonly ParentCategory[] | undefined): CategoryTree {
  const roots: CategoryNode[] = [];
  const byId = new Map<string, CategoryNode>();

  for (const parent of parents ?? []) {
    const root: CategoryNode = {
      id: parent.id,
      label: parent.descriptionTranslated,
      iconName: parent.icon,
      parentId: null,
      rootId: parent.id,
      depth: 1,
      children: [],
    };
    roots.push(root);
    byId.set(root.id, root);

    // Attach the direct (depth-2) children first so deeper entries find their
    // parent regardless of the server's flattening order.
    const direct: CategoryDto[] = [];
    const deeper: CategoryDto[] = [];
    for (const child of parent.children) {
      (child.parentId == null || child.parentId === parent.id ? direct : deeper).push(child);
    }
    for (const child of direct) {
      const node: CategoryNode = {
        id: child.id,
        label: child.descriptionTranslated,
        iconName: child.icon ?? parent.icon,
        parentId: parent.id,
        rootId: parent.id,
        depth: 2,
        children: [],
      };
      root.children.push(node);
      byId.set(node.id, node);
    }
    for (const child of deeper) {
      // Malformed data (a parentId outside this subtree) degrades to the root —
      // the entry still renders, just without the middle level.
      const attachTo = (child.parentId && byId.get(child.parentId)) || root;
      const node: CategoryNode = {
        id: child.id,
        label: child.descriptionTranslated,
        iconName: child.icon ?? attachTo.iconName ?? parent.icon,
        parentId: attachTo.id,
        rootId: parent.id,
        depth: attachTo.depth + 1,
        children: [],
      };
      attachTo.children.push(node);
      byId.set(node.id, node);
    }
  }

  return { roots, byId };
}

/** Root-first ancestry — `[Alimentação, Restaurantes, Delivery]` for a grandchild. Empty for unknown ids. */
export function categoryPath(tree: CategoryTree, id: string): CategoryNode[] {
  const path: CategoryNode[] = [];
  let node = tree.byId.get(id);
  while (node) {
    path.unshift(node);
    node = node.parentId ? tree.byId.get(node.parentId) : undefined;
  }
  return path;
}

/**
 * Breadcrumb label for ids deeper than 2 levels — `"Restaurantes › Delivery"`
 * (the root is omitted: these labels render in root-scoped contexts). Null for
 * roots, direct children and unknown ids, where the bare label suffices.
 */
export function categoryPathLabel(tree: CategoryTree, id: string): string | null {
  const path = categoryPath(tree, id);
  if (path.length < 3) return null;
  return path.slice(1).map((node) => node.label).join(' › ');
}

/**
 * Resolves a flat category ref into the `(rootId, childId)` shape category-detail
 * navigation expects. Custom categories are always root-level refs. A Pluggy id can be
 * unresolvable if the taxonomy hasn't loaded yet (or failed) — callers MUST treat `null`
 * as "can't navigate yet" rather than falling back to some default, since silently
 * navigating to the wrong category (or none) is worse than surfacing the failure.
 */
export function resolveCategoryDetailTarget(
  categoryId: string,
  source: CategoryRefSource,
  tree: CategoryTree,
): { rootId: string; childId: string | null } | null {
  if (source === 'custom') return { rootId: categoryId, childId: null };
  const node = tree.byId.get(categoryId);
  if (!node) return null;
  return { rootId: node.rootId, childId: node.depth === 1 ? null : categoryId };
}
